import { useState } from "react";
import { Users, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { EventWaitlistDTO } from "@/pages/admin/events/types";
import { axiosInstance } from "@/pages/auth/auth.context";
import { toast } from "@/components/ui/use-toast";

interface EventWaitlistCardProps {
  hasWaitlist: boolean;
  currentWaitlistSize: number | null;
  maxWaitlistSize: number | null;
  waitlistedUsers: EventWaitlistDTO[];
  eventId: string;
  onUserRemoved: () => void;
}

export default function EventWaitlistCard({
  hasWaitlist,
  currentWaitlistSize,
  maxWaitlistSize,
  waitlistedUsers,
  eventId,
  onUserRemoved,
}: EventWaitlistCardProps) {
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  const removeUserFromWaitlist = async (userId: string) => {
    setRemovingUserId(userId);
    try {
      const response = await axiosInstance.delete(
        `waitlist/event/${eventId}/user/${userId}`,
      );

      if (response.data.success) {
        toast({
          title: "User Removed",
          description:
            response.data.message || "User removed from waitlist successfully.",
          variant: "success",
        });
        onUserRemoved();
      } else {
        toast({
          title: "Error",
          description:
            response.data.message || "Failed to remove user from waitlist.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error removing user from waitlist:", error);
      let errorMessage = "Failed to remove user from waitlist.";
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        errorMessage = axiosError.response?.data?.message || errorMessage;
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setRemovingUserId(null);
    }
  };

  if (!hasWaitlist) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          Event Waitlist
          <span className="text-sm font-normal text-muted-foreground">
            ({currentWaitlistSize || 0}
            {maxWaitlistSize ? ` / ${maxWaitlistSize}` : ""})
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {waitlistedUsers && waitlistedUsers.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {waitlistedUsers.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell className="font-medium">
                    {user.firstName} {user.lastName}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Button
                      onClick={() => removeUserFromWaitlist(user.userId)}
                      disabled={removingUserId === user.userId}
                      variant="outline"
                      size="sm"
                      className="gap-2"
                    >
                      {removingUserId === user.userId ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-muted-foreground text-center py-4">
            No users currently on the waitlist.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
